/**
 * Activity transformers
 *
 * Pure functions that normalize raw API / Dexie objects into the
 * canonical ActivityItem shape consumed by buildActivityFeed and
 * all timeline/history UI components.
 *
 * ActivityItem shape:
 * {
 *   id:          string
 *   type:        'note' | 'visit' | 'prayer' | 'event'
 *   title:       string          — primary display text
 *   subtitle:    string          — secondary context (entity label, status, preview)
 *   timestamp:   string          — ISO-compatible, used for sorting (newest first)
 *   entityType:  string | null   — linked entity type (person/family/church/…)
 *   entityId:    string | null
 *   entityLabel: string | null   — denormalized entity name for display
 *   route:       string          — navigation target
 *   metadata:    Record<string, any>  — type-specific extras (not displayed directly)
 * }
 */

/** @param {Object} note — raw Dexie note */
export function transformNote(note) {
  const hasLink = note.linked_type && note.linked_type !== "general";
  return {
    id: note.id,
    type: "note",
    title: note.title || "Nota sin título",
    subtitle: hasLink && note.linked_label
      ? `Vinculada · ${note.linked_label}`
      : (note.content || "").slice(0, 60).replace(/\s+/g, " ").trim() || "",
    timestamp: note.updated_locally_at || note.updated_at || note.created_at || "",
    entityType:  hasLink ? note.linked_type  : null,
    entityId:    hasLink ? note.linked_id    : null,
    entityLabel: hasLink ? note.linked_label : null,
    route: `/notes/${note.id}`,
    metadata: {
      category:    note.category,
      is_reference: note.is_reference,
      tags:        note.tags,
    },
  };
}

/** @param {Object} visit — raw API visit */
export function transformVisit(visit) {
  return {
    id: visit.id,
    type: "visit",
    title: visit.contact_name || visit.person_name || "Visita pastoral",
    subtitle: visit.status === "realizada" ? "Realizada" : "Pendiente",
    timestamp: visit.date || visit.created_at || "",
    entityType:  visit.church_id  ? "church"  : null,
    entityId:    visit.church_id  || null,
    entityLabel: visit.church_name || null,
    route: `/visits/${visit.id}`,
    metadata: {
      status:    visit.status,
      church_id: visit.church_id,
      notes:     visit.notes,
    },
  };
}

/** @param {Object} prayer — raw API prayer */
export function transformPrayer(prayer) {
  const answered = prayer.status === "respondida";
  return {
    id: prayer.id,
    type: "prayer",
    title: prayer.text || prayer.title || "Petición de oración",
    subtitle: answered ? "Respondida" : "Activa",
    timestamp: prayer.date || prayer.created_at || "",
    entityType:  prayer.person_id ? "person" : prayer.family_id ? "family" : null,
    entityId:    prayer.person_id || prayer.family_id || null,
    entityLabel: null,
    route: `/prayers/${prayer.id}/edit`,
    metadata: {
      status:    prayer.status,
      category:  prayer.category,
      person_id: prayer.person_id,
      family_id: prayer.family_id,
    },
  };
}

/** @param {Object} event — raw API event (church / field / personal) */
export function transformEvent(event) {
  return {
    id: event.id,
    type: "event",
    title: event.title || "Evento ministerial",
    subtitle: event.event_type || event.type || "",
    timestamp: event.date || event.created_at || "",
    entityType:  event.church_id ? "church" : null,
    entityId:    event.church_id || null,
    entityLabel: null,
    route: `/agenda`,
    metadata: {
      event_type: event.event_type,
      type:       event.type,
      church_id:  event.church_id,
      end_date:   event.end_date,
    },
  };
}
